/**
 * Valori ammessi dal BE per area geografica competenza (contesto OXS).
 */
export const AREA_GEOGRAFICA_OPTIONS = [
  { value: 'NAZIONALE', label: 'NAZIONALE' },
  { value: 'REGIONALE', label: 'REGIONALE' },
  { value: 'PROVINCIALE', label: 'PROVINCIALE' },
];

export const AREA_GEOGRAFICA_ALLOWED = new Set(
  AREA_GEOGRAFICA_OPTIONS.map((option) => option.value),
);

/**
 * Stato iniziale filtri ricerca fiduciari in UI.
 * `suPortale` default NO come requisito di business.
 */
export const EMPTY_FIDUCIARI_FILTERS = {
  tipoPerizia: '',
  suPortale: 'NO',
  areaGeograficaCompetenza: '',
  areaGeograficaId: '',
  cap: '',
};

/**
 * Normalizza input CAP: tiene solo cifre e massimo 5 char.
 * @param {string} raw
 * @returns {string}
 */
export const sanitizeCapInput = (raw) => (raw || '').replace(/[^0-9]/g, '').slice(0, 5);
/**
 * Verifica CAP valido italiano/spagnolo nel formato richiesto dal BE (5 cifre).
 * @param {string} cap
 * @returns {boolean}
 */
export const isValidCap = (cap) => /^\d{5}$/.test(cap || '');

/**
 * Traduce lo stato UI nei filtri di richiesta verso la thunk di ricerca.
 * - Portal SI: invia solo `tipoPerizia` + `cap`.
 * - Portal NO (OXS): invia tipo/competenza/specializzazione/area geografica.
 */
export const buildSearchFilters = ({
  filters,
  tipo,
  idTipoIncarico,
  competenzeValue,
  idCompetenza,
  specializzazioneValue,
  idSpecializzazione,
}) => {
  if (filters.suPortale === 'SI') {
    return {
      suPortale: 'SI',
      tipoPerizia: filters.tipoPerizia,
      cap: filters.cap,
    };
  }

  return {
    suPortale: 'NO',
    // UI label stays "Tipo incarico", API expects tipoFiduciario UUID
    tipoFiduciario: tipo || idTipoIncarico || '',
    competenza: competenzeValue || idCompetenza || '',
    specializzazione: specializzazioneValue || idSpecializzazione || '',
    areaGeograficaCompetenza: filters.areaGeograficaCompetenza,
    areaGeograficaId: filters.areaGeograficaId || '',
  };
};

export const requiresAreaGeograficaSelection = (areaGeograficaCompetenza) => ['REGIONALE', 'PROVINCIALE']
  .includes(areaGeograficaCompetenza);

export const requiresAreaGeograficaIdForSearch = (areaGeograficaCompetenza) => ['NAZIONALE', 'REGIONALE', 'PROVINCIALE']
  .includes(areaGeograficaCompetenza);

export const hasRequiredFilters = (searchFilters) => {
  if (searchFilters.suPortale === 'SI') {
    return !!searchFilters.tipoPerizia && isValidCap(searchFilters.cap);
  }

  const hasBaseRequiredFields = !!searchFilters.tipoFiduciario
    && !!searchFilters.competenza
    && !!searchFilters.areaGeograficaCompetenza;
  if (!hasBaseRequiredFields) {
    return false;
  }

  if (requiresAreaGeograficaIdForSearch(searchFilters.areaGeograficaCompetenza)) {
    return !!searchFilters.areaGeograficaId;
  }

  return true;
};

/**
 * Applica reset selettivo dei campi quando cambia il toggle Portale.
 * Mantiene coerenti i filtri dei due contesti (Portal/OXS).
 */
export const getFiltersOnPortalToggle = (prevFilters, nextPortalValue) => ({
  ...prevFilters,
  suPortale: nextPortalValue,
  tipoPerizia: nextPortalValue === 'NO' ? '' : prevFilters.tipoPerizia,
  areaGeograficaCompetenza: nextPortalValue === 'SI' ? '' : prevFilters.areaGeograficaCompetenza,
  areaGeograficaId: '',
  cap: nextPortalValue === 'NO' ? '' : prevFilters.cap,
});

/**
 * Codice ISO della nazione che deve restare in testa all'elenco competenze.
 */
export const NAZIONE_PRIORITARIA_CODICE = 'IT';

const getAreaGeograficaLabel = (item) => item?.descrizione || item?.codice || '';

const isNazionePrioritaria = (item) => (item?.codice || '').trim().toUpperCase() === NAZIONE_PRIORITARIA_CODICE
  || getAreaGeograficaLabel(item).trim().toUpperCase() === 'ITALIA';

/**
 * Costruisce le option del filtro Nazione ordinate alfabeticamente,
 * mantenendo l'Italia sempre in prima posizione (CSP-3352).
 *
 * @param {Array<{id: string, codice?: string, descrizione?: string}>} nazioni aree geografiche di tipo NAZIONE.
 * @returns {Array<{value: string, label: string}>} option pronte per la DropDownList.
 */
export const buildNazioniOptions = (nazioni) => [...(nazioni || [])]
  .sort((a, b) => {
    const priorityDelta = Number(isNazionePrioritaria(b)) - Number(isNazionePrioritaria(a));
    if (priorityDelta !== 0) {
      return priorityDelta;
    }
    return getAreaGeograficaLabel(a)
      .localeCompare(getAreaGeograficaLabel(b), 'it', { sensitivity: 'base' });
  })
  .map((item) => ({ value: item.id, label: getAreaGeograficaLabel(item) }));
